// TOEFL iBT Mock 8 — Reading (2026 format, two timed modules)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, memos).
//   academic – Read an Academic Passage: short passage, 5 questions
//              (the last question is a "where does this sentence fit?" item;
//              the [A]–[D] markers are part of the passage text).
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2. Unlike Mocks 2–7,
// `upper` and `lower` are different sets, so the adaptive routing is real.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

// ── Module 1 ─────────────────────────────────────────────────
const module1 = [
  {
    id: "m1-words",
    type: "words",
    title: "The Water Cycle",
    text: `The water cycle describes the continuous movement of water between the oceans, the air, and the land. Heat from [[the]] sun [[causes]] water [[to]] evaporate [[from]] lakes [[and]] oceans, [[and]] the [[vapor]] rises [[into]] the [[atmosphere]]. As [[it]] cools, [[it]] condenses [[into]] tiny [[droplets]] that [[form]] clouds. When the droplets grow heavy enough, they fall back to Earth as rain or snow, and the cycle begins again.`,
  },
  {
    id: "m1-daily1",
    type: "daily",
    title: "Fitness Center Locker Renewal",
    text: `Fitness Center Notice: All annual locker rentals expire on August 31. Members who wish to keep their current locker must renew at the front desk or online by August 25. The renewal fee is $40 for students and $60 for staff. After August 25, any lockers that have not been renewed will be released to the waiting list, and their contents will be removed and held at the front desk for two weeks. Items not collected after that time will be donated. Members who renew online will receive a confirmation email, which should be shown at the desk to collect a new lock.`,
    questions: [
      {
        question: "What is the main purpose of the notice?",
        options: [
          "To announce a new fitness class",
          "To remind members to renew their lockers",
          "To explain a fee increase for staff",
          "To introduce a waiting list for new members",
        ],
        correct: 1,
      },
      {
        question: "What happens to items in lockers that are not renewed?",
        options: [
          "They are donated immediately.",
          "They are thrown away on August 31.",
          "They are kept at the front desk for a limited time.",
          "They are mailed to the members.",
        ],
        correct: 2,
      },
      {
        question: "What must members who renew online do to get a new lock?",
        options: [
          "Show their confirmation email at the desk",
          "Pay an additional fee",
          "Join the waiting list",
          "Renew again in person",
        ],
        correct: 0,
      },
    ],
  },
  {
    id: "m1-daily2",
    type: "daily",
    title: "Volunteer Tutor Email",
    text: `Subject: Your tutoring schedule

Hi Marcus,

Thank you for volunteering with the Westside Reading Program this semester. You have been matched with two fourth-grade students, and your sessions will take place on Tuesdays and Thursdays from 3:30 to 4:30 p.m. in Room 6 of the community center. Please arrive ten minutes early on your first day so that I can introduce you to the students' teacher.

Before you begin, all volunteers must complete a short online safety course. It takes about forty minutes, and you will not be able to start tutoring until it is finished. If Tuesdays no longer work for you, let me know by Friday, and I will try to find another time.

Best,
Elena Ruiz
Volunteer Coordinator`,
    questions: [
      {
        question: "Why did Elena write to Marcus?",
        options: [
          "To ask him to teach a fourth-grade class",
          "To give him details about his volunteer assignment",
          "To thank him for completing a safety course",
          "To change the location of his sessions",
        ],
        correct: 1,
      },
      {
        question: "What must Marcus do before he can start tutoring?",
        options: [
          "Meet the students' parents",
          "Visit Room 6 on Friday",
          "Finish an online safety course",
          "Choose a new time slot",
        ],
        correct: 2,
      },
      {
        question: "Why is Marcus asked to arrive early on his first day?",
        options: [
          "To complete paperwork",
          "To set up the room",
          "To take the safety course",
          "To be introduced to the students' teacher",
        ],
        correct: 3,
      },
    ],
  },
  {
    id: "m1-academic",
    type: "academic",
    title: "The Silk Road and the Exchange of Ideas",
    text: `The Silk Road was not a single road but a vast network of trade routes that connected China with Central Asia, the Middle East, and the Mediterranean from roughly the second century BCE. Although it takes its name from Chinese silk, a luxury highly prized in Rome, the network carried a remarkable variety of goods, including spices, glass, horses, paper, and precious metals.

Few merchants traveled the entire length of the routes. [A] Instead, goods typically passed through many hands, moving in stages from one trading city to the next. [B] Oasis towns such as Samarkand and Kashgar flourished as places where caravans rested, exchanged goods, and resupplied. These cities became cosmopolitan centers in which people of many languages and religions lived and worked side by side.

Perhaps the most significant cargo of the Silk Road was intangible. Buddhism spread from India into China along these routes, carried by monks and merchants who built monasteries and translated texts. [C] Technologies moved as well. Papermaking, developed in China, reached the Islamic world by the eighth century and later spread to Europe, where it eventually made the printing revolution possible.

The routes also transmitted less welcome passengers. Historians widely believe that the plague known as the Black Death reached Europe in the fourteenth century partly through trade networks linking Central Asia with Mediterranean ports. [D] The same connections that enriched societies culturally and economically thus also exposed them to new dangers.`,
    questions: [
      {
        question: "The word \"intangible\" in the passage is closest in meaning to",
        options: ["valuable", "not physical", "dangerous", "rare"],
        correct: 1,
      },
      {
        question: "According to paragraph 2, how were goods usually transported along the Silk Road?",
        options: [
          "By a single merchant traveling the whole route",
          "Mainly by sea between China and Rome",
          "In stages, passing between traders in different cities",
          "By government officials from China",
        ],
        correct: 2,
      },
      {
        question: "What can be inferred from paragraph 3 about papermaking?",
        options: [
          "It was invented in Europe and spread east.",
          "It was used mainly by Buddhist monks in India.",
          "It reached Europe before it reached the Islamic world.",
          "Its spread contributed to later developments in Europe.",
        ],
        correct: 3,
      },
      {
        question: "Why does the author mention the Black Death in paragraph 4?",
        options: [
          "To show that trade networks could spread harm as well as benefits",
          "To explain why the Silk Road was closed",
          "To argue that Central Asian cities were unhealthy",
          "To describe the end of the Roman Empire",
        ],
        correct: 0,
      },
      {
        question: "Where would the following sentence best fit? \"A single bale of silk might change owners a dozen times before it reached the Mediterranean.\"",
        options: ["[A]", "[B]", "[C]", "[D]"],
        correct: 1,
      },
    ],
  },
];

// ── Harder Module 2 ──────────────────────────────────────────
const upper = [
  {
    id: "up-words",
    type: "words",
    title: "Photosynthesis",
    text: `Photosynthesis is the process by which plants, algae, and some bacteria convert light energy into chemical energy. [[Inside]] specialized [[structures]] called [[chloroplasts]], light [[is]] absorbed [[by]] pigments, [[chiefly]] chlorophyll, [[and]] used [[to]] split [[water]] molecules. [[The]] oxygen [[released]] in [[this]] reaction [[escapes]] into [[the]] air. The captured energy is then used to turn carbon dioxide into sugars, which the organism stores or uses for growth. Nearly all life on Earth ultimately depends on this process.`,
  },
  {
    id: "up-daily1",
    type: "daily",
    title: "Research Study Recruitment",
    text: `PARTICIPANTS NEEDED
The Department of Psychology is recruiting adults aged 18–35 for a study on attention and multitasking. Participants will complete computer-based tasks in two sessions of approximately 50 minutes each, held one week apart in the Behavioral Sciences Building. Compensation is $15 per session; participants who complete both sessions will receive an additional $10 bonus.

You are not eligible if you have taken part in any multitasking study conducted by the department within the past twelve months, or if you require corrective lenses but cannot wear them during testing. To sign up, complete the screening questionnaire on the department website. Eligible applicants will be contacted within five business days.`,
    questions: [
      {
        question: "What is the most a participant can earn from the study?",
        options: ["$15", "$25", "$30", "$40"],
        correct: 3,
      },
      {
        question: "Who would NOT be eligible for the study?",
        options: [
          "A 34-year-old graduate student",
          "Someone who took part in a department multitasking study eight months ago",
          "Someone who wears glasses during the tasks",
          "A person who has never taken part in research",
        ],
        correct: 1,
      },
      {
        question: "What can be inferred about people who complete the questionnaire?",
        options: [
          "They will be paid for completing it.",
          "They must attend a session the same week.",
          "Not all of them will be invited to participate.",
          "They will receive the bonus automatically.",
        ],
        correct: 2,
      },
    ],
  },
  {
    id: "up-daily2",
    type: "daily",
    title: "Water Shutoff Notice",
    text: `NOTICE TO RESIDENTS — Maple Terrace Apartments

To replace a corroded main valve, the water supply to Buildings A and B will be shut off on Wednesday from 9:00 a.m. until approximately 3:00 p.m. Building C is served by a separate line and will not be affected. Residents are advised to store drinking water in advance; bottled water will also be available in the lobby of Building C throughout the day.

When service is restored, water may appear discolored for a short period. This is caused by sediment disturbed during the repair and is not harmful, but residents should run their cold taps for several minutes before use. Please avoid running washing machines or dishwashers until the water runs clear, as sediment may damage these appliances.`,
    questions: [
      {
        question: "Why will the water be shut off?",
        options: [
          "To clean the water tanks",
          "To test a new line in Building C",
          "To install new appliances",
          "To replace a damaged valve",
        ],
        correct: 3,
      },
      {
        question: "The word \"restored\" in the notice is closest in meaning to",
        options: ["brought back", "reduced", "inspected", "paid for"],
        correct: 0,
      },
      {
        question: "Why should residents avoid using dishwashers right after the repair?",
        options: [
          "The water will be too hot.",
          "The water will be harmful to drink.",
          "Particles in the water could damage the machines.",
          "Building C needs the water supply.",
        ],
        correct: 2,
      },
    ],
  },
  {
    id: "up-academic",
    type: "academic",
    title: "Echolocation in Bats",
    text: `Many bats navigate and hunt in complete darkness using echolocation, a biological form of sonar. A bat emits a rapid series of high-frequency calls, most of them far above the range of human hearing, and then listens for the echoes that bounce back from objects in its environment. By analyzing the delay and quality of these echoes, the bat constructs a detailed acoustic picture of its surroundings.

The precision of this system is extraordinary. Some species can detect wires thinner than a human hair and distinguish between insects of different shapes while flying at speed. [A] As a bat closes in on its prey, it increases the rate of its calls dramatically, from roughly ten per second during searching to as many as two hundred per second in the final moments before capture. [B] This final burst, sometimes called a feeding buzz, provides continuous updates on the insect's position.

Producing such loud calls poses a problem: a bat's own voice could deafen it. Many species solve this by contracting a small muscle in the middle ear an instant before each call, temporarily reducing their sensitivity, and relaxing it in time to hear the returning echo. [C] This rapid cycle of contraction and relaxation is among the fastest muscle movements known in mammals.

Echolocation has also shaped an evolutionary contest with prey. Certain moths have evolved ears tuned to bat frequencies and respond to approaching calls by diving or flying erratically. [D] Some tiger moths go further, producing clicks of their own that appear to interfere with the bat's ability to process echoes. Such adaptations suggest that echolocation has driven innovation not only in bats but also in the animals they hunt.`,
    questions: [
      {
        question: "The word \"erratically\" in the passage is closest in meaning to",
        options: ["quickly", "unpredictably", "silently", "downward"],
        correct: 1,
      },
      {
        question: "According to paragraph 2, why does a bat increase its call rate as it approaches prey?",
        options: [
          "To receive constant information about the insect's location",
          "To frighten the insect into flying more slowly",
          "To warn other bats away from the prey",
          "To save energy before the capture",
        ],
        correct: 0,
      },
      {
        question: "What can be inferred from paragraph 3 about the middle-ear muscle?",
        options: [
          "It allows the bat to produce louder calls.",
          "It stays contracted throughout a hunt.",
          "It protects the bat's hearing without blocking the echoes.",
          "It developed only in species that hunt moths.",
        ],
        correct: 2,
      },
      {
        question: "Why does the author mention tiger moths in paragraph 4?",
        options: [
          "To explain how moths locate food at night",
          "To argue that bats rarely succeed in catching moths",
          "To compare moth hearing with human hearing",
          "To give an example of prey that actively counter echolocation",
        ],
        correct: 3,
      },
      {
        question: "Where would the following sentence best fit? \"Such fine discrimination allows bats to select the most nutritious prey and ignore falling leaves or debris.\"",
        options: ["[A]", "[B]", "[C]", "[D]"],
        correct: 0,
      },
    ],
  },
];

// ── Easier Module 2 ──────────────────────────────────────────
const lower = [
  {
    id: "low-words",
    type: "words",
    title: "Recycling",
    text: `Recycling means turning used materials into new products. When [[people]] recycle [[paper]], it [[is]] cleaned [[and]] made [[into]] new [[paper]] products. [[Glass]] and [[metal]] can [[be]] melted [[down]] and [[used]] [[again]] [[many]] times. Recycling saves energy and reduces the amount of waste sent to landfills.`,
  },
  {
    id: "low-daily1",
    type: "daily",
    title: "Farmers Market Flyer",
    text: `GREEN VALLEY FARMERS MARKET
Every Saturday from 8:00 a.m. to 1:00 p.m. in the Town Hall parking lot.
Fresh fruit, vegetables, bread, cheese, and flowers from local farms.
Free live music from 10:00 to 11:00 a.m.
Bring your own bags — plastic bags are no longer provided.
Dogs are welcome but must be kept on a leash.`,
    questions: [
      {
        question: "Where is the market held?",
        options: ["In a park", "In the Town Hall parking lot", "At a local farm", "In a school gym"],
        correct: 1,
      },
      {
        question: "What should shoppers bring?",
        options: ["Their own bags", "Cash only", "A ticket", "Their own music"],
        correct: 0,
      },
      {
        question: "What is true about dogs at the market?",
        options: [
          "They are not allowed.",
          "They can visit only after 1:00 p.m.",
          "They must be on a leash.",
          "They need a special pass.",
        ],
        correct: 2,
      },
    ],
  },
  {
    id: "low-daily2",
    type: "daily",
    title: "Text from a Roommate",
    text: `Hey Sam! I'm going to be late tonight because my bus broke down. Can you take the pizza out of the oven at 7:15? It should be ready by then. Also, my mom might call the apartment phone. If she does, please tell her I'll call her back after dinner. Thanks! — Jordan`,
    questions: [
      {
        question: "Why will Jordan be late?",
        options: [
          "Jordan has extra classes.",
          "Jordan missed the train.",
          "Jordan's bus broke down.",
          "Jordan is shopping for pizza.",
        ],
        correct: 2,
      },
      {
        question: "What does Jordan ask Sam to do at 7:15?",
        options: [
          "Order a pizza",
          "Take the pizza out of the oven",
          "Call Jordan's mom",
          "Meet Jordan at the bus stop",
        ],
        correct: 1,
      },
      {
        question: "What should Sam say if Jordan's mom calls?",
        options: [
          "That Jordan will call back after dinner",
          "That Jordan is at the bus stop",
          "That dinner is ready",
          "That Jordan's phone is broken",
        ],
        correct: 0,
      },
    ],
  },
  {
    id: "low-academic",
    type: "academic",
    title: "Why We Yawn",
    text: `Almost everyone yawns, often several times a day. Yawning involves opening the mouth wide, breathing in deeply, and then breathing out slowly. People yawn when they are tired or bored, but scientists are still not completely sure why yawning happens.

For many years, people believed that we yawn because our bodies need more oxygen. [A] However, experiments have not supported this idea. When people breathed air with extra oxygen, they did not yawn any less. [B]

A newer idea is that yawning helps to cool the brain. When we yawn, cool air enters the mouth and nose, and blood flow to the head increases. [C] Studies have found that people yawn more often in cool weather than in very hot weather, which supports this explanation.

Yawning is also contagious. Seeing, hearing, or even reading about yawning can make a person yawn. [D] Some scientists think this may be connected to empathy, the ability to understand how others feel, because people tend to catch yawns more easily from friends and family than from strangers.`,
    questions: [
      {
        question: "The word \"contagious\" in the passage is closest in meaning to",
        options: ["easily spread", "painful", "dangerous", "rare"],
        correct: 0,
      },
      {
        question: "According to paragraph 2, what did experiments show about oxygen?",
        options: [
          "Extra oxygen made people yawn more.",
          "Extra oxygen did not reduce yawning.",
          "People yawn only when oxygen is low.",
          "Oxygen cools the brain.",
        ],
        correct: 1,
      },
      {
        question: "According to paragraph 3, when do people yawn more often?",
        options: ["In very hot weather", "After exercise", "In cool weather", "Only at night"],
        correct: 2,
      },
      {
        question: "Why does the author mention friends and family in paragraph 4?",
        options: [
          "To show that yawns spread more easily between people who are close",
          "To explain why people yawn when they are tired",
          "To show that strangers never yawn",
          "To describe a way to stop yawning",
        ],
        correct: 0,
      },
      {
        question: "Where would the following sentence best fit? \"Some animals, such as dogs, can even catch yawns from humans.\"",
        options: ["[A]", "[B]", "[C]", "[D]"],
        correct: 3,
      },
    ],
  },
];

export const readingModules = { module1, upper, lower };

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}
